import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { useCurrentModule } from '../contexts/ModuleContext';
import { Layout } from '../components/Layout';
import AnnouncementTicker from '../components/AnnouncementTicker';
import { StatusBadge, PriorityBadge } from '../components/StatusBadge';
import { formatDate } from '../utils/dateUtils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import {
  Ticket,
  Clock,
  CheckCircle,
  DollarSign,
  Plus,
  Search,
  FolderOpen,
  UserCheck,
  ClipboardCheck,
  FileCheck,
  XCircle,
  Bell,
  AlertCircle
} from 'lucide-react';
import { DateHierarchyFilter } from '../components/DateHierarchyFilter';
import DepartmentSelectorModal from '../components/DepartmentSelectorModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REQUEST_TYPES = [
  "Stationery Request",
  "Courier (Incoming / Outgoing)",
  "Office Assistant Help",
  "Guest Arrival",
  "Organize an Event",
  "Onboarding",
  "Meeting Room Booking",
  "Other"
];

export default function DashboardPage() {
  const navigate = useNavigate();
  const { getAuthHeader, employee, user } = useAuth();
  const { desktopPermission, requestDesktopPermission } = useNotifications();
  const [stats, setStats] = useState(null);
  const [recentTickets, setRecentTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [showNotifBanner, setShowNotifBanner] = useState(true);
  const [deptModalOpen, setDeptModalOpen] = useState(false);
  const [itDashboard, setITDashboard] = useState(null); // IT operational dashboard data
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [requestTypeFilter, setRequestTypeFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const role = employee?.role || 'Employee';
  const isAdminHead = role === 'Admin Head' || role === 'Super Admin';
  const isManagement = role === 'Management';
  const isAdminTeam = role === 'Admin Team';
  const isAdminOrManagement = ['Admin Head', 'Super Admin', 'Management'].includes(role);
  const canCreateTicket = !['Admin Head', 'Super Admin', 'Management'].includes(role);
  const canSeeITDashboard = ['IT Team', 'IT Head', 'Super Admin', 'Management'].includes(role);
  const isITPrimary = role === 'IT Team' || role === 'IT Head';
  // IT Recent Tickets filters (client-side — applied only to the IT recent table)
  const [itRecentSearch, setItRecentSearch] = useState('');
  const [itRecentStatus, setItRecentStatus] = useState('all');
  const [itRecentPriority, setItRecentPriority] = useState('all');
  const [itRecentLocation, setItRecentLocation] = useState('all');
  const [itRecentAssignee, setItRecentAssignee] = useState('all');
  // IT Team / IT Head land on IT as the primary slicer; everyone else defaults to Admin
  const [moduleFilter, setModuleFilter] = useState(isITPrimary ? 'IT' : 'Admin'); // Admin | IT
  const showITView = moduleFilter === 'IT' && canSeeITDashboard;
  // Sync moduleFilter -> global ModuleContext so the top-left header reflects it
  const { setCurrentModule } = useCurrentModule();
  useEffect(() => { setCurrentModule(moduleFilter); }, [moduleFilter, setCurrentModule]);

  const handleEnableNotifications = async () => {
    const permission = await requestDesktopPermission();
    if (permission === 'granted') {
      setShowNotifBanner(false);
    }
  };

  const fetchDepartments = useCallback(async () => {
    try {
      const res = await fetch(`${API}/departments`);
      if (res.ok) {
        const data = await res.json();
        setDepartments(data);
      }
    } catch (error) {
      console.error('Fetch departments error:', error);
    }
  }, []);

  const fetchDashboardData = useCallback(async () => {
    try {
      const dateParams = `${dateFrom ? `&date_from=${dateFrom}` : ''}${dateTo ? `&date_to=${dateTo}` : ''}`;
      const moduleParam = moduleFilter !== 'all' ? `&module=${moduleFilter}` : '';
      const [statsRes, ticketsRes] = await Promise.all([
        fetch(`${API}/dashboard/stats?_=1${dateParams}${moduleParam}`, {
          headers: getAuthHeader(),
        }),
        fetch(`${API}/dashboard/recent-tickets?limit=10${
          statusFilter !== 'all' ? `&status=${statusFilter}` : ''
        }${
          departmentFilter !== 'all' ? `&department=${encodeURIComponent(departmentFilter)}` : ''
        }${
          requestTypeFilter !== 'all' ? `&request_type=${encodeURIComponent(requestTypeFilter)}` : ''
        }${
          priorityFilter !== 'all' ? `&priority=${priorityFilter}` : ''
        }${dateParams}${moduleParam}`, {
          headers: getAuthHeader(),
        })
      ]);

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }

      if (ticketsRes.ok) {
        const ticketsData = await ticketsRes.json();
        setRecentTickets(ticketsData);
      }
    } catch (error) {
      console.error('Dashboard fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, statusFilter, departmentFilter, requestTypeFilter, priorityFilter, moduleFilter, dateFrom, dateTo]);

  const fetchITDashboard = useCallback(async () => {
    if (!canSeeITDashboard) return;
    try {
      const res = await fetch(`${API}/it/dashboard`, { headers: getAuthHeader() });
      if (res.ok) setITDashboard(await res.json());
    } catch (e) {
      console.error('IT dashboard fetch error:', e);
    }
  }, [getAuthHeader, canSeeITDashboard]);

  useEffect(() => {
    fetchDepartments();
    if (showITView) {
      setLoading(true);
      fetchITDashboard().finally(() => setLoading(false));
    } else {
      fetchDashboardData();
    }
  }, [fetchDashboardData, fetchDepartments, fetchITDashboard, showITView]);

  const filteredTickets = recentTickets.filter(ticket => {
    if (!searchQuery) return true;
    return (
      ticket.ticket_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.request_type.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.requester_name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const KPICard = ({ title, value, icon: Icon, color, delay }) => {
    return (
      <Card 
        className={`kpi-card card-hover animate-fade-in animate-delay-${delay}`}
        data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">{title}</p>
              <p className="text-3xl font-bold mt-2">{value ?? '-'}</p>
            </div>
            <div className={`p-3 rounded-xl ${color}`}>
              <Icon className="w-6 h-6" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderKPIs = () => {
    if (isAdminOrManagement) {
      // Admin Head and Management view - full dashboard
      return (
        <>
          <KPICard 
            title="Total Tickets" 
            value={stats?.total_tickets} 
            icon={Ticket} 
            color="bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"
            delay="100"
          />
          <KPICard 
            title="Open" 
            value={stats?.open_tickets} 
            icon={FolderOpen} 
            color="bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400"
            delay="200"
          />
          <KPICard 
            title="Approved" 
            value={stats?.approved_tickets} 
            icon={FileCheck} 
            color="bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400"
            delay="300"
          />
          <KPICard 
            title="In Progress" 
            value={stats?.in_progress_tickets} 
            icon={Clock} 
            color="bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400"
            delay="400"
          />
          <KPICard 
            title="Completed" 
            value={stats?.completed_tickets} 
            icon={CheckCircle} 
            color="bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400"
            delay="100"
          />
          <KPICard 
            title="Rejected" 
            value={stats?.rejected_tickets} 
            icon={XCircle} 
            color="bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400"
            delay="200"
          />
          {stats?.total_expense !== null && (
            <KPICard 
              title="Total Expenses" 
              value={`₹${(stats?.total_expense || 0).toLocaleString()}`} 
              icon={DollarSign} 
              color="bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400"
              delay="300"
            />
          )}
        </>
      );
    }
    
    if (isAdminTeam) {
      // Admin Team view - similar to employee but with task KPIs
      return (
        <>
          <KPICard 
            title="My Tickets" 
            value={stats?.my_tickets} 
            icon={Ticket} 
            color="bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"
            delay="100"
          />
          <KPICard 
            title="Tasks Assigned to Me" 
            value={stats?.tasks_assigned_to_me} 
            icon={UserCheck} 
            color="bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400"
            delay="200"
          />
          <KPICard 
            title="Tasks Completed by Me" 
            value={stats?.tasks_completed_by_me} 
            icon={ClipboardCheck} 
            color="bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400"
            delay="300"
          />
          <KPICard 
            title="Open" 
            value={stats?.open_tickets} 
            icon={FolderOpen} 
            color="bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400"
            delay="400"
          />
          <KPICard 
            title="In Progress" 
            value={stats?.in_progress_tickets} 
            icon={Clock} 
            color="bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400"
            delay="100"
          />
          <KPICard 
            title="Completed" 
            value={stats?.completed_tickets} 
            icon={CheckCircle} 
            color="bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400"
            delay="200"
          />
        </>
      );
    }

    // Regular Employee view
    return (
      <>
        <KPICard 
          title="My Tickets" 
          value={stats?.my_tickets} 
          icon={Ticket} 
          color="bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"
          delay="100"
        />
        <KPICard 
          title="Open" 
          value={stats?.open_tickets} 
          icon={FolderOpen} 
          color="bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400"
          delay="200"
        />
        <KPICard 
          title="In Progress" 
          value={stats?.in_progress_tickets} 
          icon={Clock} 
          color="bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400"
          delay="300"
        />
        <KPICard 
          title="Completed" 
          value={stats?.completed_tickets} 
          icon={CheckCircle} 
          color="bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400"
          delay="400"
        />
        {/* Pending Approvals - only for employees who are managers */}
        {stats?.pending_approvals > 0 && (
          <KPICard 
            title="Pending Approvals" 
            value={stats?.pending_approvals} 
            icon={AlertCircle} 
            color="bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400"
            delay="100"
          />
        )}
      </>
    );
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="dashboard-page">
        <AnnouncementTicker />
        {/* Desktop Notification Permission Banner */}
        {showNotifBanner && desktopPermission !== 'granted' && (
          <Alert className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
            <Bell className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <AlertDescription className="flex items-center justify-between w-full">
              <span className="text-blue-800 dark:text-blue-200">
                Enable desktop notifications to get real-time updates on your tickets
              </span>
              <div className="flex gap-2 ml-4">
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => setShowNotifBanner(false)}
                  className="text-xs"
                >
                  Later
                </Button>
                <Button 
                  size="sm"
                  onClick={handleEnableNotifications}
                  className="bg-blue-600 hover:bg-blue-700 text-xs"
                  data-testid="enable-notifications-btn"
                >
                  Enable Notifications
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
              Hi, {user?.name?.split(' ')[0] || 'there'}!
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground mt-1">
              Here's an overview of your tickets
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Department / Module slicer — visible to all roles */}
            <div className="flex items-center gap-1 border rounded-md p-1 bg-card" data-testid="dept-slicer">
              {[
                { v: 'Admin', l: 'Admin' },
                { v: 'IT', l: 'IT' },
              ].map(opt => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setModuleFilter(opt.v)}
                  data-testid={`dept-slicer-${opt.v.toLowerCase()}`}
                  className={`px-3 py-1 text-xs rounded font-medium transition ${
                    moduleFilter === opt.v
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-muted'
                  }`}
                >
                  {opt.l}
                </button>
              ))}
            </div>
            <DateHierarchyFilter
              onDateChange={(from, to) => {
                setDateFrom(from);
                setDateTo(to);
              }}
            />
            {canCreateTicket && (
              <Button 
                onClick={() => setDeptModalOpen(true)}
                className="gap-2 btn-press"
                data-testid="create-ticket-btn"
              >
                <Plus className="w-4 h-4" />
                Create Ticket
              </Button>
            )}
          </div>
        </div>

        {/* KPI Cards — Admin view OR IT operational view based on slicer */}
        {!showITView ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-4">
            {loading ? (
              <>
                {[1, 2, 3, 4, 5, 6].map(i => (
                  <Card key={i}>
                    <CardContent className="p-5">
                      <Skeleton className="h-4 w-20 mb-3" />
                      <Skeleton className="h-7 w-14" />
                    </CardContent>
                  </Card>
                ))}
              </>
            ) : (
              renderKPIs()
            )}
          </div>
        ) : (
          // IT Operational KPIs — replaces Admin KPIs when slicer is IT
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="it-kpi-grid">
            {loading || !itDashboard ? (
              [...Array(7)].map((_, i) => (
                <Card key={i}><CardContent className="p-5"><Skeleton className="h-4 w-20 mb-3" /><Skeleton className="h-7 w-14" /></CardContent></Card>
              ))
            ) : (
              <>
                <KPICard title="Total Tickets" value={itDashboard.kpis.total_tickets} icon={FolderOpen} color="bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300" delay="100" />
                <KPICard title="Open" value={itDashboard.kpis.open} icon={FolderOpen} color="bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400" delay="100" />
                <KPICard title="In Progress" value={itDashboard.kpis.in_progress} icon={Clock} color="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" delay="100" />
                <KPICard title="Completed" value={itDashboard.kpis.completed} icon={CheckCircle} color="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" delay="200" />
                <KPICard title="Escalated" value={itDashboard.kpis.escalated} icon={Bell} color="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" delay="200" />
                <KPICard title="SLA Breaches" value={itDashboard.kpis.sla_breaches} icon={AlertCircle} color="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" delay="200" />
                <KPICard
                  title="Avg Resolution"
                  value={(() => {
                    const m = itDashboard.kpis.avg_resolution_minutes;
                    if (!m || m <= 0) return '—';
                    const h = Math.floor(m / 60);
                    const r = Math.round(m % 60);
                    return `${String(h).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
                  })()}
                  icon={Clock}
                  color="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400"
                  delay="300"
                />
              </>
            )}
          </div>
        )}

        {/* Recent Tickets — Admin/legacy view */}
        {!showITView && (
        <Card className="animate-fade-in">
          <CardHeader className="flex flex-col gap-4">
            <div className="flex flex-row items-center justify-between">
              <CardTitle>Recent Tickets</CardTitle>
              <Button variant="outline" size="sm" asChild>
                <Link to={isAdminOrManagement ? "/admin/queue" : "/tickets"}>View All</Link>
              </Button>
            </div>
            
            {/* Filters */}
            <div className="flex flex-wrap gap-2">
              <div className="relative flex-1 min-w-[160px] sm:flex-none">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 w-full sm:w-[180px]"
                  data-testid="search-input"
                />
              </div>
              
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="flex-1 min-w-[120px] sm:flex-none sm:w-[130px]" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="Open">Open</SelectItem>
                  <SelectItem value="Approved">Approved</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                  <SelectItem value="Rejected">Rejected</SelectItem>
                  <SelectItem value="Cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>

              {isAdminOrManagement && (
                <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                  <SelectTrigger className="flex-1 min-w-[130px] sm:flex-none sm:w-[150px]" data-testid="department-filter">
                    <SelectValue placeholder="Department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {departments.map(dept => (
                      <SelectItem key={dept.department_id} value={dept.name}>
                        {dept.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              <Select value={requestTypeFilter} onValueChange={setRequestTypeFilter}>
                <SelectTrigger className="flex-1 min-w-[130px] sm:flex-none sm:w-[150px]" data-testid="request-type-filter">
                  <SelectValue placeholder="Request Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {REQUEST_TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="flex-1 min-w-[110px] sm:flex-none sm:w-[120px]" data-testid="priority-filter">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priority</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="Low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : filteredTickets.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground empty-state">
                <Ticket className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No tickets found</p>
                {canCreateTicket && (
                  <Button 
                    variant="link" 
                    onClick={() => setDeptModalOpen(true)}
                    className="mt-2"
                  >
                    Create your first ticket
                  </Button>
                )}
              </div>
            ) : (
              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead>Ticket ID</TableHead>
                      <TableHead>Request Type</TableHead>
                      {isAdminOrManagement && (
                        <TableHead className="hidden md:table-cell">Department</TableHead>
                      )}
                      <TableHead className="hidden md:table-cell">Requester</TableHead>
                      <TableHead className="hidden sm:table-cell">Priority</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="hidden lg:table-cell">Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredTickets.map((ticket) => (
                      <TableRow 
                        key={ticket.ticket_id} 
                        className="table-row-hover cursor-pointer"
                        onClick={() => navigate(`/tickets/${ticket.ticket_id}`)}
                      >
                        <TableCell>
                          <span className="ticket-id-link font-mono text-sm">
                            {ticket.ticket_id}
                          </span>
                        </TableCell>
                        <TableCell className="font-medium">
                          {ticket.request_type === 'Other' 
                            ? ticket.other_request_type 
                            : ticket.request_type}
                        </TableCell>
                        {isAdminOrManagement && (
                          <TableCell className="hidden md:table-cell">
                            <span
                              data-testid={`dept-badge-${ticket.ticket_id}`}
                              className={`inline-block text-[11px] px-2 py-0.5 rounded-full font-medium ${
                                (ticket.module || 'Admin') === 'IT'
                                  ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                                  : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                              }`}
                            >
                              {ticket.module || 'Admin'}
                            </span>
                          </TableCell>
                        )}
                        <TableCell className="hidden md:table-cell text-muted-foreground">
                          {ticket.requester_name}
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          <PriorityBadge priority={ticket.priority} />
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={ticket.status} />
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-muted-foreground text-sm">
                          {formatDate(ticket.created_at)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Recent IT Tickets — operational view shown when slicer = IT */}
        {showITView && (
          <Card className="animate-fade-in" data-testid="it-recent-tickets">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Recent IT Tickets</CardTitle>
                <p className="text-xs text-muted-foreground mt-1">Last 10 IT tickets in your scope</p>
              </div>
              <Button variant="outline" size="sm" asChild>
                <Link to="/it/queue">View IT Queue</Link>
              </Button>
            </CardHeader>
            <CardContent>
              {/* Local filters for the Recent IT Tickets table */}
              {!loading && itDashboard && (itDashboard.recent_tickets || []).length > 0 && (() => {
                const recent = itDashboard.recent_tickets || [];
                const locations = Array.from(new Set(recent.map(t => t.ticket_location).filter(Boolean))).sort();
                const assignees = Array.from(new Set(recent.map(t => t.assigned_it_name).filter(Boolean))).sort();
                return (
                  <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="it-recent-filters">
                    <div className="relative flex-1 min-w-[160px] sm:flex-none">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Search IT tickets..."
                        value={itRecentSearch}
                        onChange={(e) => setItRecentSearch(e.target.value)}
                        className="pl-9 w-full sm:w-[200px]"
                        data-testid="it-recent-search"
                      />
                    </div>
                    <Select value={itRecentStatus} onValueChange={setItRecentStatus}>
                      <SelectTrigger className="flex-1 min-w-[120px] sm:flex-none sm:w-[140px]" data-testid="it-recent-status">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Statuses</SelectItem>
                        <SelectItem value="Open">Open</SelectItem>
                        <SelectItem value="In Progress">In Progress</SelectItem>
                        <SelectItem value="Escalated">Escalated</SelectItem>
                        <SelectItem value="Completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={itRecentPriority} onValueChange={setItRecentPriority}>
                      <SelectTrigger className="flex-1 min-w-[110px] sm:flex-none sm:w-[120px]" data-testid="it-recent-priority">
                        <SelectValue placeholder="Priority" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Priorities</SelectItem>
                        <SelectItem value="High">High</SelectItem>
                        <SelectItem value="Medium">Medium</SelectItem>
                        <SelectItem value="Low">Low</SelectItem>
                      </SelectContent>
                    </Select>
                    {locations.length > 1 && (
                      <Select value={itRecentLocation} onValueChange={setItRecentLocation}>
                        <SelectTrigger className="flex-1 min-w-[130px] sm:flex-none sm:w-[150px]" data-testid="it-recent-location">
                          <SelectValue placeholder="Location" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Locations</SelectItem>
                          {locations.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                    {assignees.length > 1 && (
                      <Select value={itRecentAssignee} onValueChange={setItRecentAssignee}>
                        <SelectTrigger className="flex-1 min-w-[140px] sm:flex-none sm:w-[180px]" data-testid="it-recent-assignee">
                          <SelectValue placeholder="Assignee" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Assignees</SelectItem>
                          <SelectItem value="__unassigned__">(Unassigned)</SelectItem>
                          {assignees.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                    {(itRecentSearch || itRecentStatus !== 'all' || itRecentPriority !== 'all' || itRecentLocation !== 'all' || itRecentAssignee !== 'all') && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setItRecentSearch('');
                          setItRecentStatus('all');
                          setItRecentPriority('all');
                          setItRecentLocation('all');
                          setItRecentAssignee('all');
                        }}
                        data-testid="it-recent-clear"
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                );
              })()}
              {loading || !itDashboard ? (
                <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>
              ) : (itDashboard.recent_tickets || []).length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Ticket className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No IT tickets in your scope.</p>
                </div>
              ) : (() => {
                const recent = itDashboard.recent_tickets || [];
                const filteredRecent = recent.filter(t => {
                  if (itRecentStatus !== 'all' && t.status !== itRecentStatus) return false;
                  if (itRecentPriority !== 'all' && t.priority !== itRecentPriority) return false;
                  if (itRecentLocation !== 'all' && (t.ticket_location || '') !== itRecentLocation) return false;
                  if (itRecentAssignee === '__unassigned__' && t.assigned_it_name) return false;
                  if (itRecentAssignee !== 'all' && itRecentAssignee !== '__unassigned__' && t.assigned_it_name !== itRecentAssignee) return false;
                  if (itRecentSearch) {
                    const q = itRecentSearch.toLowerCase();
                    const blob = `${t.ticket_id} ${t.request_type} ${t.requester_name || ''} ${t.assigned_it_name || ''} ${t.ticket_location || ''}`.toLowerCase();
                    if (!blob.includes(q)) return false;
                  }
                  return true;
                });
                if (filteredRecent.length === 0) {
                  return (
                    <div className="text-center py-8 text-muted-foreground" data-testid="it-recent-empty-filtered">
                      <p>No IT tickets match the current filters.</p>
                    </div>
                  );
                }
                return (
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead>Ticket</TableHead>
                        <TableHead>Request Type</TableHead>
                        <TableHead className="hidden md:table-cell">Requester</TableHead>
                        <TableHead className="hidden md:table-cell">Location</TableHead>
                        <TableHead className="hidden lg:table-cell">Assigned</TableHead>
                        <TableHead className="hidden sm:table-cell">Priority</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="hidden lg:table-cell">Created</TableHead>
                        <TableHead>Completion</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredRecent.map(t => {
                        const m = t.completion_minutes;
                        const compStr = (!m || m <= 0)
                          ? ''
                          : `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(Math.round(m % 60)).padStart(2, '0')}`;
                        return (
                          <TableRow
                            key={t.ticket_id}
                            className="table-row-hover cursor-pointer"
                            onClick={() => navigate(`/tickets/${t.ticket_id}`)}
                          >
                            <TableCell><span className="font-mono text-sm text-primary hover:underline">{t.ticket_id}</span></TableCell>
                            <TableCell className="font-medium">{t.request_type}</TableCell>
                            <TableCell className="hidden md:table-cell text-muted-foreground">{t.requester_name}</TableCell>
                            <TableCell className="hidden md:table-cell text-muted-foreground">{t.ticket_location || '-'}</TableCell>
                            <TableCell className="hidden lg:table-cell text-xs">{t.assigned_it_name || <span className="text-muted-foreground">Unassigned</span>}</TableCell>
                            <TableCell className="hidden sm:table-cell"><PriorityBadge priority={t.priority} /></TableCell>
                            <TableCell><StatusBadge status={t.status} /></TableCell>
                            <TableCell className="hidden lg:table-cell text-muted-foreground text-sm">{formatDate(t.created_at)}</TableCell>
                            <TableCell className="font-mono text-xs">{compStr}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                );
              })()}
            </CardContent>
          </Card>
        )}
      </div>
      <DepartmentSelectorModal open={deptModalOpen} onOpenChange={setDeptModalOpen} />
    </Layout>
  );
}
